import { useState, useCallback, useEffect } from "react";
import { DEFAULT_SCORING, DEFAULT_SETTINGS, PLATFORM_PRESETS } from '../data/scoring';
import { sanitizePositionDials, DEFAULT_POSITION_DIALS } from '../utils/positionShares';
import { sanitizeMarketAnchor, DEFAULT_MARKET_ANCHOR } from '../utils/marketAnchor';

const DEFAULT_LEAGUE = {
  name: "My Auction League",
  scoring: {
    ...DEFAULT_SCORING,
    passYdsPer: 20, passTD: 6, passInt: -2, receptions: 0.5,
    passYdsBonus: 5, passYdsBonusThreshold: 350,
    rushYdsBonus: 5, rushYdsBonusThreshold: 150,
    recYdsBonus: 5, recYdsBonusThreshold: 150,
    fumbles: 0, fumblesLost: -2,
  },
  positions: { ...DEFAULT_SETTINGS.positions },
  budget: 200, teams: 10, activePreset: null, activeModifiers: ["6PT Pass TD"],
};

export default function useLeagueSettings() {
  // One-time init: load saved leagues + restore the user's last-selected league
  // (persisted by the effect below). If that league still exists, settings /
  // activeLeagueName / tiers all initialize from it, so the app defaults to it
  // on reload instead of forcing re-selection. If it was deleted, fall back to
  // defaults and drop the stale key.
  const [{ savedLeagues: initSaved, restored: initRestored }] = useState(() => {
    let saved;
    try {
      saved = JSON.parse(localStorage.getItem("auctionArchitect_savedLeagues")) || [DEFAULT_LEAGUE];
    } catch {
      saved = [DEFAULT_LEAGUE];
    }
    let restored = null;
    try {
      const name = localStorage.getItem("auctionArchitect_activeLeague");
      if (name) {
        const found = saved.find((l) => l.name === name);
        if (found) restored = found;
        else localStorage.removeItem("auctionArchitect_activeLeague"); // stale (league deleted)
      }
    } catch { /* ignore */ }
    return { savedLeagues: saved, restored };
  });

  // positionDials lives on `settings` and is MIRRORED to the active league.
  // Settings is the source of truth on purpose: updateActiveLeague no-ops
  // without an activeLeagueName, so a league-only field would be invisible to
  // every user who runs a mock without saving a league — which is most of them,
  // and which is the exact reach problem this feature exists to solve.
  //
  // Absent on every league saved before today → sanitize returns all-1.0 →
  // buildUserPositionMultipliers returns null → the OFF path. Legacy leagues
  // are unaffected by construction.
  const [settings, setSettings] = useState(() =>
    initRestored
      ? {
          budget: initRestored.budget, teams: initRestored.teams,
          scoring: { ...initRestored.scoring }, positions: { ...initRestored.positions },
          positionDials: sanitizePositionDials(initRestored.positionDials),
          // Same shape, same story: absent on every league saved before today
          // → sanitize returns both-null → buildMarketAnchor returns null → the
          // OFF path. Sanitize-on-read IS the migration; there is no version
          // stamp to bump and legacy leagues are unaffected by construction.
          marketAnchor: sanitizeMarketAnchor(initRestored.marketAnchor),
        }
      : {
          ...DEFAULT_SETTINGS, positions: { ...DEFAULT_SETTINGS.positions },
          positionDials: { ...DEFAULT_POSITION_DIALS },
          marketAnchor: { ...DEFAULT_MARKET_ANCHOR },
        }
  );
  const [showSettings, setShowSettings] = useState(false);
  const [savedLeagues, setSavedLeagues] = useState(initSaved);
  const [activeLeagueName, setActiveLeagueName] = useState(initRestored ? initRestored.name : null);

  const loadTierOverridesForLeague = useCallback((league) => {
    try {
      const key = "userTierSheet_" + (league || "default");
      const raw = localStorage.getItem(key);
      if (!raw) return {};
      const pairs = JSON.parse(raw);
      if (Array.isArray(pairs)) {
        const obj = {};
        pairs.forEach(([k, v]) => { obj[k] = v; });
        return obj;
      }
      if (typeof pairs === "object" && pairs !== null) return pairs;
    } catch { /* ignore */ }
    return {};
  }, []);

  const [userTierOverrides, setUserTierOverrides] = useState(
    () => loadTierOverridesForLeague(initRestored ? initRestored.name : null)
  );
  const userTierOverrideCount = Object.keys(userTierOverrides).length;

  // Persist the last-selected league so it defaults on next load. Removed when
  // no league is active (null).
  useEffect(() => {
    try {
      if (activeLeagueName) localStorage.setItem("auctionArchitect_activeLeague", activeLeagueName);
      else localStorage.removeItem("auctionArchitect_activeLeague");
    } catch { /* ignore */ }
  }, [activeLeagueName]);

  // The list itself now lives in src/data/scoring.js so /rate's standalone team
  // tab reads the same presets; the exported name here is unchanged.
  const PLATFORM_DEFAULTS = PLATFORM_PRESETS;

  // Confirmation for a completed league import, shown on the underlying screen
  // AFTER the modal closes. Holding the user in the modal to read a success
  // message read as "nothing happened" and they kept clicking Import.
  // Shape: { name, summary } — see importSummary() in SettingsModal.
  const [importNotice, setImportNotice] = useState(null);

  // Auto-dismiss. Keyed on the notice object so a second import restarts the
  // clock instead of inheriting the first one's remaining time.
  useEffect(() => {
    if (!importNotice) return;
    const t = setTimeout(() => setImportNotice(null), 6000);
    return () => clearTimeout(t);
  }, [importNotice]);

  // Note: does NOT clear roster — caller is responsible for setRoster({}) after calling these.
  // Always closes the modal. `notice` is what an import passes to raise the
  // confirmation toast outside it; the Apply Settings button passes nothing.
  const applySettings = useCallback((newSettings, { notice = null } = {}) => {
    setSettings(newSettings);
    setShowSettings(false);
    if (notice) setImportNotice(notice);
  }, []);

  const loadLeagueFromList = useCallback((league) => {
    setSettings({
      budget: league.budget, teams: league.teams,
      scoring: { ...league.scoring }, positions: { ...league.positions },
      // Read the league's own copy back, so two saved leagues keep separate
      // dials rather than inheriting whatever was last on screen.
      positionDials: sanitizePositionDials(league.positionDials),
      marketAnchor: sanitizeMarketAnchor(league.marketAnchor),
    });
    setActiveLeagueName(league.name);
  }, []);

  // Patch fields on the active saved league in place. Spreads the existing
  // record, so keys this function knows nothing about (auctionHistory,
  // activeModifiers, whatever comes later) survive untouched — the same
  // contract SettingsModal's updateLeague relies on. No-ops when there's no
  // active league, which is the hand-built/unsaved case.
  const updateActiveLeague = useCallback((patch) => {
    setSavedLeagues(prev => {
      if (!activeLeagueName) return prev;
      const next = prev.map(l => l.name === activeLeagueName ? { ...l, ...patch } : l);
      try {
        localStorage.setItem("auctionArchitect_savedLeagues", JSON.stringify(next));
      } catch { /* quota/private-mode — in-memory state still updates */ }
      return next;
    });
  }, [activeLeagueName]);

  /**
   * Move the dials. Writes `settings` (the source of truth) and mirrors to the
   * active league when there is one, so the choice survives a league reload.
   * updateActiveLeague no-ops without an activeLeagueName, so the unsaved-league
   * case is handled by that function rather than by a branch here.
   */
  const setPositionDials = useCallback((dials) => {
    const clean = sanitizePositionDials(dials);
    setSettings(prev => ({ ...prev, positionDials: clean }));
    updateActiveLeague({ positionDials: clean });
  }, [updateActiveLeague]);

  /**
   * Set the market anchor — the user's two dollar answers about what the top of
   * their market clears at. Writes `settings` (the source of truth) and mirrors
   * to the active league, exactly as setPositionDials does and for the same
   * reason: updateActiveLeague no-ops without an activeLeagueName, so a
   * league-only field would be invisible to everyone running an unsaved mock.
   */
  const setMarketAnchor = useCallback((anchor) => {
    const clean = sanitizeMarketAnchor(anchor);
    setSettings(prev => ({ ...prev, marketAnchor: clean }));
    updateActiveLeague({ marketAnchor: clean });
  }, [updateActiveLeague]);

  const loadDefaultPlatform = useCallback((platform) => {
    const scoring = { ...DEFAULT_SCORING, ...platform.changes };
    const positions = { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 1, SFLEX: 0, DEF: 1, K: 1, BN: 6 };
    // ⚠️ This builder has never carried `positionDials` — benign (absent → null
    // → off) but it means picking a platform default silently drops the dials.
    // Noted at chunk 0; the anchor is included here rather than repeating the
    // gap, and the dial omission stays a separate fix.
    setSettings({ budget: 200, teams: 10, scoring, positions, marketAnchor: { ...DEFAULT_MARKET_ANCHOR } });
    setActiveLeagueName(platform.name);
  }, []);

  return {
    settings, setSettings,
    showSettings, setShowSettings,
    savedLeagues, setSavedLeagues,
    updateActiveLeague,
    activeLeagueName, setActiveLeagueName,
    loadTierOverridesForLeague,
    userTierOverrides, setUserTierOverrides,
    userTierOverrideCount,
    PLATFORM_DEFAULTS,
    importNotice, setImportNotice,
    applySettings,
    loadLeagueFromList,
    loadDefaultPlatform,
    setPositionDials,
    setMarketAnchor,
  };
}
